import { format } from 'date-fns';

/**
 * One-time pickups are Poland-only, so the pickup window is reasoned about in
 * Polish local time regardless of the merchant's browser timezone.
 */
const TIMEZONE = 'Europe/Warsaw';

/** Earliest hour a courier window may start. */
export const MIN_PICKUP_HOUR = 9;

/** Minimum gap (hours) between the window's "from" and "until". */
export const MIN_GAP_HOURS = 1;

/** Fallback latest hour when InPost returns no cut-off for the postal code. */
export const DEFAULT_CUTOFF_HOUR = 16;

/** How far ahead the day picker allows selecting. */
const MAX_DAYS_AHEAD = 60;

type WarsawNow = { date: string; hour: number };

/** Current date (YYYY-MM-DD) and hour in Europe/Warsaw. */
function warsawNow(): WarsawNow {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const lookup = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  // Intl emits "24" for midnight in some engines; normalise to 0.
  const hour = Number(lookup('hour')) % 24;

  return { date: `${lookup('year')}-${lookup('month')}-${lookup('day')}`, hour };
}

function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

function range(start: number, end: number): number[] {
  if (end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/** Earliest selectable "from" hour for a given day (later today, MIN_PICKUP_HOUR otherwise). */
function earliestFromHour(isoDate: string, now: WarsawNow): number {
  if (isoDate !== now.date) return MIN_PICKUP_HOUR;
  return Math.max(MIN_PICKUP_HOUR, now.hour + 1);
}

/**
 * Calendar matcher (react-day-picker) for days that cannot be picked: weekends,
 * past days, today once it's too late to fit a window before the cut-off, and
 * days beyond the booking horizon. Reasoned in Europe/Warsaw.
 */
export function pickupDayMatcher(cutoffHour: number): (date: Date) => boolean {
  const now = warsawNow();
  const maxIso = format(addDaysIso(now.date, MAX_DAYS_AHEAD), 'yyyy-MM-dd');

  return (date: Date) => {
    if (isWeekend(date)) return true;

    const iso = format(date, 'yyyy-MM-dd');
    if (iso < now.date || iso > maxIso) return true;
    if (iso === now.date && earliestFromHour(iso, now) > cutoffHour - MIN_GAP_HOURS) return true;

    return false;
  };
}

function addDaysIso(isoDate: string, days: number): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day + days);
}

export function fromHourOptions(isoDate: string, cutoffHour: number): number[] {
  const now = warsawNow();
  return range(earliestFromHour(isoDate, now), cutoffHour - MIN_GAP_HOURS);
}

export function toHourOptions(fromHour: number, cutoffHour: number): number[] {
  return range(fromHour + MIN_GAP_HOURS, cutoffHour);
}

export function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * Render an ISO timestamp's clock time in Polish local time, so the pickup window
 * reads correctly regardless of the admin's browser timezone.
 */
export function formatWarsawClock(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}
